import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "../../../ui/select";
import type { NipDestinationDialCode } from "./types";
import { isValidNipDestinationDialCode } from "./idDataValidation";

const DIAL_CODE_OPTIONS = [
  {
    value: "+52",
    country: "México",
    flags: ["🇲🇽"],
  },
  {
    value: "+1",
    country: "Estados Unidos / Canadá",
    flags: ["🇺🇸", "🇨🇦"],
  },
] as const satisfies ReadonlyArray<{
  value: NipDestinationDialCode;
  country: string;
  flags: readonly string[];
}>;

interface NipDialCodeSelectProps {
  id: string;
  value: NipDestinationDialCode;
  onValueChange: (value: NipDestinationDialCode) => void;
  disabled?: boolean;
  invalid?: boolean;
  describedBy?: string;
}

function FlagGroup({ flags }: { flags: readonly string[] }) {
  return (
    <span aria-hidden="true" className="flex shrink-0 -space-x-1">
      {flags.map((flag) => (
        <span
          key={flag}
          className="flex h-6 w-6 items-center justify-center rounded-full border-2 border-white bg-slate-100 text-sm leading-none"
        >
          {flag}
        </span>
      ))}
    </span>
  );
}

export function NipDialCodeSelect({
  id,
  value,
  onValueChange,
  disabled = false,
  invalid = false,
  describedBy,
}: NipDialCodeSelectProps) {
  const selectedOption =
    DIAL_CODE_OPTIONS.find((option) => option.value === value) ??
    DIAL_CODE_OPTIONS[0];

  return (
    <Select
      value={value}
      onValueChange={(nextValue) => {
        if (isValidNipDestinationDialCode(nextValue)) {
          onValueChange(nextValue);
        }
      }}
      disabled={disabled}
    >
      <SelectTrigger
        id={id}
        aria-invalid={invalid || undefined}
        aria-describedby={describedBy}
        className={`h-11 rounded-lg bg-white px-3 focus:ring-[#28397a]/20 ${
          invalid ? "border-red-500" : "border-slate-200"
        }`}
      >
        <div className="flex min-w-0 items-center gap-2.5">
          <FlagGroup flags={selectedOption.flags} />
          <span className="font-semibold tabular-nums text-slate-800">
            {selectedOption.value}
          </span>
          <span className="sr-only">{selectedOption.country}</span>
        </div>
      </SelectTrigger>
      <SelectContent
        align="start"
        className="min-w-[17rem] rounded-xl border-slate-200 shadow-[0_16px_36px_-16px_rgba(15,23,42,0.35)]"
      >
        {DIAL_CODE_OPTIONS.map((option) => (
          <SelectItem
            key={option.value}
            value={option.value}
            className="h-12 rounded-lg pl-8 pr-3 focus:bg-slate-100"
          >
            <span className="inline-flex items-center gap-3">
              <FlagGroup flags={option.flags} />
              <span className="font-medium text-slate-800">
                {option.country}
              </span>
              <span className="font-semibold tabular-nums text-slate-500">
                {option.value}
              </span>
            </span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
